import { makeAutoObservable } from 'mobx';
import {
  InvoiceDetailInterface,
  InvoiceListFilteredWiseInterface,
  InputInvoiceDetailInterface,
  CustomerInvoiceDataInterface,
  CustomerBillableHoursDetailInterface,
  CustomerInvoiceTimeframesInterface
} from '../../interfaces/InvoiceInterface';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import { nanoid } from 'nanoid';

// Initial state for invoice input detail
const initialInvoiceState: InputInvoiceDetailInterface = {
  companyId: '',
  currency: '',
  customer: {
    phone: '',
    lastName: '',
    id: '',
    firstName: '',
    companyName: '',
    email: '',
    companyId: '',
    address: '',
    status: undefined,
    projectId: ''
  },
  date: new Date(),
  discount: 0,
  dueDate: new Date().toISOString(),
  dueDays: '',
  emails: [],
  isSendMeCopy: false,
  items: [
    {
      id: nanoid(),
      index: 100,
      amount: 0,
      desc: '',
      isTax: true,
      isTax2: true,
      itemType: 1,
      projectId: {
        id: '',
        key: '',
        name: '',
        value: ''
      },
      qty: 1,
      timeLogs: [],
      unitPrice: 0
    }
  ],
  notes: '',
  number: '',
  status: 0,
  subTotal: 0,
  tax: [
    {
      id: '',
      name: 'Tax',
      value: 0
    }
  ],
  totalAmount: 0,
  totalDiscount: 0,
  totalTax: 0,
  totalTax2: 0,
  id: ''
};

// Initial state for billable hours detail
const initialBillableState: CustomerBillableHoursDetailInterface = {
  customerId: '',
  type: '1',
  billableHoursType: 1,
  billableHoursTimeframe: { text: '', value: '' },
  displayHoursType: 1,
  displayHoursOption: { project: true, task: true, people: true, date: true, notes: false },
  projects: []
};

// Initial state for invoice view data
const initialInvoiceViewData: InvoiceDetailInterface = {
  companyId: '',
  currency: '',
  customer: {
    phone: '',
    lastName: '',
    id: '',
    firstName: '',
    companyName: '',
    email: '',
    companyId: '',
    address: '',
    status: undefined,
    projectId: ''
  },
  date: '',
  discount: 0,
  dueAmount: 0,
  dueDate: '',
  dueDays: 0,
  id: '',
  items: [],
  number: '',
  status: 0,
  statusText: '',
  tax: [],
  totalAmount: 0,
  totalDiscount: 0,
  totalTax: 0,
  totalTax2: 0,
  subTotal: 0
};

export class InvoiceStore {
  loading = false;
  invoices: InvoiceListFilteredWiseInterface[] = [];
  invoiceViewData: InvoiceDetailInterface = initialInvoiceViewData;
  customerInvoiceData: CustomerInvoiceDataInterface[] = [];
  inputInvoiceDetail: InputInvoiceDetailInterface = {
    ...initialInvoiceState,
    customer: { ...(initialInvoiceState.customer || {}) } as CustomerDetailsInterface,
    items: initialInvoiceState.items.map((item) => ({ ...item, projectId: { ...item.projectId } })),
    tax: initialInvoiceState.tax.map((taxItem) => ({ ...taxItem }))
  };
  customerBillableHoursDetail: CustomerBillableHoursDetailInterface = {
    ...initialBillableState,
    billableHoursTimeframe: { ...initialBillableState.billableHoursTimeframe },
    displayHoursOption: { ...initialBillableState.displayHoursOption }
  };

  constructor() {
    makeAutoObservable(this);
  }

  // Set loading state
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  // Set invoice list
  setInvoices(invoices: InvoiceListFilteredWiseInterface[]) {
    this.invoices = invoices;
  }

  // Set invoice view data
  setInvoiceViewData(data: InvoiceDetailInterface) {
    this.invoiceViewData = data;
  }

  // Set customer invoice data
  setCustomerInvoiceData(data: CustomerInvoiceDataInterface[]) {
    this.customerInvoiceData = data;
  }

  // Set invoice detail (input form)
  setInvoiceDetail(detail: InputInvoiceDetailInterface | InvoiceDetailInterface | null | undefined) {
    if (!detail) {
      this.inputInvoiceDetail = { ...initialInvoiceState };
      return;
    }

    // Convert InvoiceDetailInterface to InputInvoiceDetailInterface if needed
    const inputDetail: InputInvoiceDetailInterface = {
      ...initialInvoiceState,
      ...detail,
      dueDays: detail.dueDays?.toString() ?? initialInvoiceState.dueDays
    };

    this.inputInvoiceDetail = inputDetail;
  }

  // Update invoice detail property
  updateInvoiceDetail(propsName: string, value: string | number | boolean | object | undefined | null) {
    if (propsName in this.inputInvoiceDetail) {
      (this.inputInvoiceDetail as unknown as Record<string, string | number | boolean | object | undefined | null>)[
        propsName
      ] = value;
    }
  }

  // Clear invoice detail
  clearInvoiceDetail() {
    this.inputInvoiceDetail = {
      ...initialInvoiceState,
      customer: { ...initialInvoiceState.customer } as CustomerDetailsInterface,
      items: initialInvoiceState.items.map((item) => ({ ...item, projectId: { ...item.projectId } })),
      tax: initialInvoiceState.tax.map((taxItem) => ({ ...taxItem }))
    };
  }

  // Update billable hours detail property
  updateBillableHoursDetail(propsName: string, value: string[] | number | CustomerInvoiceTimeframesInterface) {
    (
      this.customerBillableHoursDetail as unknown as {
        [key: string]: string[] | number | CustomerInvoiceTimeframesInterface;
      }
    )[propsName] = value;
  }

  // Update billable hours time entry detail
  updateBillableHoursTimeEntry(propsName: string, value: boolean) {
    if (this.customerBillableHoursDetail && this.customerBillableHoursDetail.displayHoursOption) {
      (this.customerBillableHoursDetail.displayHoursOption as unknown as { [key: string]: boolean })[propsName] = value;
    }
  }

  // Clear billable hours detail
  clearBillableHoursDetail() {
    this.customerBillableHoursDetail = {
      ...initialBillableState,
      billableHoursTimeframe: { ...initialBillableState.billableHoursTimeframe },
      displayHoursOption: { ...initialBillableState.displayHoursOption }
    };
  }

  // Clear all invoice data
  clearAllInvoiceData() {
    this.loading = false;
    this.invoices = [];
    this.invoiceViewData = initialInvoiceViewData;
    this.customerInvoiceData = [];
    this.clearInvoiceDetail();
    this.clearBillableHoursDetail();
  }
}
